import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  PREVIEW_CASES,
  MEDIA_TYPE,
  EXTENSION,
} from 'src/app/core/constants/common';
import { MaxShowLetterCount } from 'src/app/shared/utilities/constants';
import { environment } from 'src/environments/environment';
import {
  ButtonActionType,
  Orientation,
  RCSLayout,
} from '../../../modules/templates/interfaces/create-template.interface';

@Component({
  selector: 'app-unified-template-preview',
  templateUrl: './unified-template-preview.component.html',
  styleUrls: ['./unified-template-preview.component.scss'],
})
export class UnifiedTemplatePreviewComponent implements OnChanges {
  @Input() channel: string = '';
  @Input() templateData: any = null;
  @Input() highlightedContent: string = '';
  @Input() rcsCarouselHighlightedContents: string[] = [];
  @Input() whatsAppCarouselHighlightedContents: string[] = [];
  @Input() activeFocusPlaceholder: string | null = null;

  // ── Expose enums to template ──────────────────────────────────────────────
  previewCase = PREVIEW_CASES;
  mediaTypeEnum = MEDIA_TYPE; // renamed to avoid conflict with the getter below
  buttonActionType = ButtonActionType;
  orientationEnum = Orientation;
  rcsLayoutEnum = RCSLayout;

  // ── RCS processed data (mirrors RcsTemplatePreviewComponent) ──────────────
  rcsFormData: any = {};
  rcsCarouselUICards: any[] = [];
  rcsMediaFileType: string = '';
  rcsPdfFileName: string = '';

  // ── Read-more state ───────────────────────────────────────────────────────
  isTextExpanded: boolean = false;

  // ── Letter-count limits ───────────────────────────────────────────────────
  readonly whatsappMaxLetterCount = MaxShowLetterCount.WHATSAPP;
  readonly smsMaxLetterCount = MaxShowLetterCount.SMS;
  readonly rcsMaxLetterCount = 80;
  readonly pushMaxLetterCount = 100;

  constructor() {}

  // ══════════════════════════════════════════════════════════════════════════
  // Lifecycle
  // ══════════════════════════════════════════════════════════════════════════
  ngOnChanges(changes: SimpleChanges): void {
    const channelChanged = !!changes['channel'];
    // Only reset expanded state when the channel changes or when templateData
    // changes to a genuinely different template (not just button-label updates).
    const templateChanged =
      !!changes['templateData'] &&
      changes['templateData'].previousValue?.templateCode !==
        changes['templateData'].currentValue?.templateCode;

    if (channelChanged || templateChanged) {
      this.isTextExpanded = false;
    }
    if (changes['templateData'] || changes['channel']) {
      this.processRcsData();
    }
  }

  private getRcsRegularCard(tpl: any): any | null {
    const list: any[] = tpl?.rcsTemplates || [];
    if (!list.length) return null;
    // Legacy preview uses [0] as regular; but if API returns carousel cards mixed in, pick first non-carousel
    return (
      list.find(
        (c) => (c?.type || '').toString().toUpperCase() !== 'CAROUSEL_CARD',
      ) || list[0]
    );
  }

  private getRcsCarouselCards(tpl: any): any[] {
    const list: any[] = tpl?.rcsTemplates || [];
    return (list || [])
      .filter(
        (c) => (c?.type || '').toString().toUpperCase() === 'CAROUSEL_CARD',
      )
      .slice()
      .sort((a, b) => Number(a?.sequence ?? 0) - Number(b?.sequence ?? 0));
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RCS data processing — mirrors RcsTemplatePreviewComponent.modifyVideoThumbnail
  // ══════════════════════════════════════════════════════════════════════════
  private processRcsData(): void {
    if (this.previewChannel !== this.previewCase.RCS || !this.templateData) {
      this.rcsFormData = {};
      this.rcsCarouselUICards = [];
      return;
    }

    const tpl = this.templateData;
    const regular = this.getRcsRegularCard(tpl);
    const carouselCards = this.getRcsCarouselCards(tpl);
    if (!regular && carouselCards.length === 0) {
      this.rcsFormData = {};
      this.rcsCarouselUICards = [];
      return;
    }

    // Legacy preview considers carousel when there are CAROUSEL_CARD entries,
    // even if the first template is REGULAR.
    if (carouselCards.length > 0) {
      this.rcsFormData = { isCarousel: true };
      // Mirror the non-isCalledFromCreation branch of RcsTemplatePreviewComponent
      this.rcsCarouselUICards = carouselCards.map((card: any) => {
        const rawUrl: string = card.mediaURL || card.mediaUrl || '';
        const isDynamic = rawUrl.includes('#mediaUrl');
        const mediaURL = isDynamic ? `#${rawUrl.split('/#')?.[1]}` : rawUrl;
        const staticUrl = isDynamic ? rawUrl.split('/#')?.[0] : '';

        const rawThumb: string = card.videoThumbnail || '';
        const isDynThumb = rawThumb.includes('#thumbnail');
        const videoThumbnail = card.videoThumbnail
          ? isDynThumb
            ? `#${rawThumb.split('/#')?.[1]}`
            : rawThumb
          : undefined;
        const staticThumbnailUrl = card.videoThumbnail
          ? isDynThumb
            ? rawThumb.split('/#')?.[0]
            : ''
          : undefined;

        return {
          ...card,
          mediaURL,
          staticUrl,
          carouselMediaType: card.mediaType,
          cardTitle: card.title,
          ...(card.videoThumbnail != null ? { videoThumbnail } : {}),
          ...(card.videoThumbnail != null ? { staticThumbnailUrl } : {}),
        };
      });
    } else {
      // Standalone rich card — mirror modifyVideoThumbnail (non-creation)
      // Normalize mediaType: API may return 'DOC', but MEDIA_TYPE.DOC = 'DOCUMENT'
      const rawMediaType = (regular?.mediaType || '').toUpperCase();
      const normalizedMediaType =
        rawMediaType === 'DOC' ? MEDIA_TYPE.DOC : rawMediaType;

    // Normalize dynamic media/thumbnail tokens so the template can
    // render the same placeholders as the legacy preview.
    const rawMediaUrl: string =
      regular?.mediaURL || regular?.mediaUrl || '';
    const isDynMediaUrl = rawMediaUrl.includes('#mediaUrl');
    const mediaUrl = isDynMediaUrl
      ? `#${rawMediaUrl.split('/#')?.[1]}`
      : rawMediaUrl;
    const staticUrl = isDynMediaUrl ? rawMediaUrl.split('/#')?.[0] : '';

    const rawThumb: string = regular?.videoThumbnail || '';
    const isDynThumb = rawThumb.includes('#thumbnail');
    const videoThumbnail = rawThumb
      ? isDynThumb
        ? `#${rawThumb.split('/#')?.[1]}`
        : rawThumb
      : undefined;
    const staticThumbnailUrl = isDynThumb
      ? rawThumb.split('/#')?.[0]
      : undefined;

      this.rcsFormData = {
        isButton: !!regular?.buttons?.length,
        buttonRequests: regular?.buttons || [],
        ...regular,
      mediaUrl,
      staticUrl,
        mediaType: normalizedMediaType,
        cardTitle: regular?.title || '',
      ...(videoThumbnail != null ? { videoThumbnail } : {}),
      ...(staticThumbnailUrl != null ? { staticThumbnailUrl } : {}),
        // Legacy PreviewModalComponent maps these two fields for RCS preview
        orientation:
          regular?.mediaAlignment ||
          regular?.orientation ||
          Orientation.VERTICAL,
        layout:
          regular?.mediaSubAlignment || regular?.layout || RCSLayout.RIGHT,
        isCarousel: false,
      };

      // Compute isMedia accurately
      this.rcsFormData.isMedia = !!(
        this.rcsFormData.mediaUrl ||
        this.rcsFormData.videoThumbnail ||
        this.rcsFormData.staticThumbnailUrl ||
        normalizedMediaType
      );

      delete this.rcsFormData.rcsTemplates;

      // Derive mediaFileType the same way as setTypeAndExtension()
      const mUrl: string = this.rcsFormData.mediaUrl;
      if (mUrl && !mUrl.includes('#mediaUrl1#')) {
        this.setRcsMediaFileType(mUrl);
      } else if (this.rcsFormData.mediaType) {
        this.rcsMediaFileType = this.rcsFormData.mediaType;
      } else {
        this.rcsMediaFileType = '';
      }
      if (
        this.rcsMediaFileType === MEDIA_TYPE.DOC &&
        mUrl &&
        !mUrl.includes('#mediaUrl1#')
      ) {
        this.rcsPdfFileName = this.getFileNameFromURL(mUrl);
      } else {
        this.rcsPdfFileName = '';
      }
      this.rcsCarouselUICards = [];
    }
  }

  private setRcsMediaFileType(mediaURL: string): void {
    const ext = mediaURL.split('.').pop()?.toUpperCase() || '';
    if (ext === EXTENSION.MP4) {
      this.rcsMediaFileType = MEDIA_TYPE.VIDEO;
    } else if (
      ext === EXTENSION.PNG ||
      ext === EXTENSION.JPEG ||
      ext === EXTENSION.JPG ||
      ext === EXTENSION.GIF
    ) {
      this.rcsMediaFileType = MEDIA_TYPE.IMAGE;
    } else {
      this.rcsMediaFileType = MEDIA_TYPE.DOC;
    }
  }

  private getFileNameFromURL(url: string): string {
    try {
      const name = url
        .slice(url.lastIndexOf('/') + 1, url.lastIndexOf('_'))
        ?.replaceAll('%20', ' ');
      const arr = url.split('.');
      return `${name}.${arr[arr.length - 1]}`;
    } catch {
      return 'Document.pdf';
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Channel / route helpers
  // ══════════════════════════════════════════════════════════════════════════
  get previewChannel(): string {
    const upper = (this.channel || '').trim().toUpperCase();
    if (upper === 'WHATSAPP') return this.previewCase.WHATSAPP;
    if (upper === 'SMS') return this.previewCase.SMS;
    if (upper === 'RCS') return this.previewCase.RCS;
    if (
      upper === 'EMAIL' ||
      upper === 'E-MAIL' ||
      upper === 'WEB_APP_NOTIFICATION'
    )
      return this.previewCase.EMAIL;
    if (
      upper === 'PUSH NOTIFICATION' ||
      upper === 'PUSH_NOTIFICATION' ||
      upper === 'PUSH'
    )
      return this.previewCase.PUSH_NOTIFICATION;
    // Already a preview-case token
    if (
      Object.values(this.previewCase).some(
        (v) => v?.toString().toUpperCase() === upper,
      )
    )
      return upper;
    return (this.channel || '').trim();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Common media / button getters (WhatsApp / Push use these)
  // ══════════════════════════════════════════════════════════════════════════
  get mediaURL(): string {
    if (!this.templateData) return '';
    if (this.previewChannel === this.previewCase.WHATSAPP)
      return (
        this.templateData.whatsAppTemplates?.[0]?.mediaURL ||
        this.templateData.whatsAppTemplates?.[0]?.mediaUrl ||
        ''
      );
    if (this.previewChannel === this.previewCase.PUSH_NOTIFICATION)
      return (
        this.templateData.pushNotificationTemplate?.mediaURL ||
        this.templateData.pushNotificationTemplate?.mediaUrl ||
        ''
      );
    return '';
  }

  get mediaType(): string {
    if (!this.templateData) return '';
    let rawType = '';
    let url = '';
    if (this.previewChannel === this.previewCase.WHATSAPP) {
      rawType = this.templateData.whatsAppTemplates?.[0]?.mediaType || '';
      url = this.templateData.whatsAppTemplates?.[0]?.mediaURL || '';
    } else if (this.previewChannel === this.previewCase.PUSH_NOTIFICATION) {
      rawType = this.templateData.pushNotificationTemplate?.mediaType || '';
      url = this.templateData.pushNotificationTemplate?.mediaURL || '';
    }
    // If API provides a mediaType, normalize and use it
    if (rawType) return rawType.toLowerCase();
    // Otherwise derive from URL extension — exactly like legacy setTypeAndExtension()
    if (url && !url.includes('#mediaUrl')) {
      const ext = (url.split('.').pop() || '').toUpperCase();
      if (ext === EXTENSION.MP4) return MEDIA_TYPE.VIDEO.toLowerCase();
      if (
        ext === EXTENSION.PNG ||
        ext === EXTENSION.JPEG ||
        ext === EXTENSION.JPG ||
        ext === EXTENSION.GIF
      )
        return MEDIA_TYPE.IMAGE.toLowerCase();
      return MEDIA_TYPE.DOC.toLowerCase();
    }
    return '';
  }

  get buttons(): any[] {
    if (!this.templateData) return [];
    if (this.previewChannel === this.previewCase.WHATSAPP)
      return this.templateData.whatsAppTemplates?.[0]?.buttons || [];
    return [];
  }

  get whatsAppCarouselCards(): any[] {
    if (!this.templateData || this.previewChannel !== this.previewCase.WHATSAPP)
      return [];
    const list: any[] = this.templateData.whatsAppTemplates || [];
    // Legacy: carousel cards are separate entries with type CAROUSEL_CARD
    return list
      .filter(
        (c) => (c?.type || '').toString().toUpperCase() === 'CAROUSEL_CARD',
      )
      .slice()
      .sort((a, b) => Number(a?.sequence ?? 0) - Number(b?.sequence ?? 0));
  }

  /** The CAROUSEL_BUBBLE entry — this is the main body text shown above the carousel cards */
  get whatsAppCarouselBubble(): any | null {
    if (!this.templateData || this.previewChannel !== this.previewCase.WHATSAPP)
      return null;
    const list: any[] = this.templateData.whatsAppTemplates || [];
    return (
      list.find(
        (c) => (c?.type || '').toString().toUpperCase() === 'CAROUSEL_BUBBLE',
      ) || null
    );
  }

  get isWhatsAppCarousel(): boolean {
    const firstType = (this.templateData?.whatsAppTemplates?.[0]?.type || '')
      .toString()
      .toLowerCase();
    // Legacy: carousel_bubble indicates WA carousel preview
    return (
      firstType === 'carousel_bubble' || this.whatsAppCarouselCards.length > 0
    );
  }

  /** Check if a WA carousel card has dynamic (unmapped) media */
  isWaCarouselDynamicMedia(card: any): boolean {
    return !!card.mediaURL && !this.isValidMediaUrl(card.mediaURL);
  }

  /** Check if the currently focused placeholder is for media */
  get isMediaFocused(): boolean {
    if (!this.activeFocusPlaceholder) return false;
    const parts = this.activeFocusPlaceholder.split(/[\.\[\]\_]/).filter(Boolean);
    const key = parts[parts.length - 1];
    return key === 'media' || key === 'mediaUrl';
  }

  get isRcsCarousel(): boolean {
    return this.rcsFormData?.isCarousel === true;
  }

  // ── RCS orientation / layout (from rcsFormData, mirrors the legacy component) ──
  get rcsOrientation(): string {
    return (
      this.rcsFormData?.orientation ||
      this.rcsFormData?.mediaAlignment ||
      'VERTICAL'
    )
      .toString()
      .toUpperCase();
  }

  get rcsLayout(): string {
    const l = (
      this.rcsFormData?.layout ||
      this.rcsFormData?.mediaSubAlignment ||
      'RIGHT'
    )
      .toString()
      .toUpperCase();
    return l === 'LEFT' ? 'LEFT' : 'RIGHT';
  }

  get cardTitle(): string {
    return this.rcsFormData?.cardTitle || this.rcsFormData?.title || '';
  }

  get pdfFileName(): string {
    return this.rcsPdfFileName;
  }

  get pushNotificationTitle(): string {
    if (
      !this.templateData ||
      this.previewChannel !== this.previewCase.PUSH_NOTIFICATION
    )
      return '';
    const t = this.templateData.pushNotificationTemplate?.title;
    return t === '#title#' ? 'Dynamic Title' : t || '';
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Read-more
  // ══════════════════════════════════════════════════════════════════════════
  toggleText(): void {
    this.isTextExpanded = !this.isTextExpanded;
  }

  get smsShouldShowReadMore(): boolean {
    const text =
      this.highlightedContent || this.templateData?.smsTemplate?.content || '';
    return this.stripHtml(text).length > this.smsMaxLetterCount;
  }

  get whatsappShouldShowReadMore(): boolean {
    const text =
      this.highlightedContent ||
      this.templateData?.whatsAppTemplates?.[0]?.content ||
      '';
    return this.stripHtml(text).length > this.whatsappMaxLetterCount;
  }

  get shouldShowRcsReadMore(): boolean {
    // Show read-more when content is long enough to be clipped by CSS max-height
    const raw = this.rcsFormData?.content || '';
    return raw.length > 60;
  }

    get pushShouldShowReadMore(): boolean {
    const text =
      this.highlightedContent ||
      this.templateData?.pushNotificationTemplate?.content ||
      '';
    return this.stripHtml(text).length > this.pushMaxLetterCount;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Text display helpers
  // ══════════════════════════════════════════════════════════════════════════
  private stripHtml(input: string): string {
    return (input || '').replace(/<[^>]*>/g, '');
  }

  getSafeClampedHtml(html: string, maxChars: number): string {
    if (!html) return '';
    if (this.stripHtml(html).length <= maxChars) return html;

    const div = document.createElement('div');
    div.innerHTML = html;
    let currentLen = 0;

    function traverse(node: Node): void {
      if (currentLen >= maxChars) {
        node.textContent = '';
        return;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        const txt = node.textContent || '';
        if (currentLen + txt.length > maxChars) {
          node.textContent = txt.slice(0, maxChars - currentLen) + '...';
          currentLen = maxChars;
        } else {
          currentLen += txt.length;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        for (const child of Array.from(node.childNodes)) {
          if (currentLen >= maxChars) (node as Element).removeChild(child);
          else traverse(child);
        }
      }
    }
    traverse(div);
    return div.innerHTML;
  }

  get displayedSmsHtml(): string {
    const h =
      this.highlightedContent || this.templateData?.smsTemplate?.content || '';
    return this.applyFocusHighlight(
      this.isTextExpanded
        ? h
        : this.getSafeClampedHtml(h, this.smsMaxLetterCount),
    );
  }

  get displayedWhatsAppHtml(): string {
    const h =
      this.highlightedContent ||
      this.templateData?.whatsAppTemplates?.[0]?.content ||
      '';
    return this.applyFocusHighlight(
      this.isTextExpanded
        ? h
        : this.getSafeClampedHtml(h, this.whatsappMaxLetterCount),
    );
  }

  get displayedRcsHtml(): string {
    // Always return full content — CSS max-height handles visual clipping
    const h = this.highlightedContent || this.rcsFormData?.content || '';
    return this.applyFocusHighlight(h);
  }

  /** Card title — always full */
  get displayedRcsCardTitle(): string {
    const t = this.rcsFormData?.cardTitle || this.rcsFormData?.title || '';
    return this.applyFocusHighlight(t);
  }

  get displayedPushHtml(): string {
    const h =
      this.highlightedContent ||
      this.templateData?.pushNotificationTemplate?.content ||
      '';
    return this.applyFocusHighlight(
      this.isTextExpanded
        ? h
        : this.getSafeClampedHtml(h, this.pushMaxLetterCount),
    );
  }

  // Carousel card text
  getRcsCarouselCardText(i: number): string {
    return (
      this.rcsCarouselHighlightedContents?.[i] ||
      this.rcsCarouselUICards?.[i]?.content ||
      ''
    );
  }

  getWhatsAppCarouselCardText(i: number): string {
    return (
      this.whatsAppCarouselHighlightedContents?.[i] ||
      this.whatsAppCarouselCards?.[i]?.content ||
      ''
    );
  }

  getDisplayedRcsCarouselHtml(i: number): string {
    const h = this.getRcsCarouselCardText(i);
    return this.applyFocusHighlight(
      this.isTextExpanded
        ? h
        : this.getSafeClampedHtml(h, this.rcsMaxLetterCount),
    );
  }

  getDisplayedWhatsAppCarouselHtml(i: number): string {
    const h = this.getWhatsAppCarouselCardText(i);
    return this.applyFocusHighlight(
      this.isTextExpanded
        ? h
        : this.getSafeClampedHtml(h, this.whatsappMaxLetterCount),
    );
  }

  getRcsCarouselCardPlainTextLength(i: number): number {
    return this.stripHtml(this.getRcsCarouselCardText(i)).length;
  }

  getWhatsAppCarouselCardPlainTextLength(i: number): number {
    return this.stripHtml(this.getWhatsAppCarouselCardText(i)).length;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Focus-highlight helper (yellow highlight for active dropdown placeholder)
  // ══════════════════════════════════════════════════════════════════════════
  applyFocusHighlight(html: string): string {
      if (!html || !this.activeFocusPlaceholder) return html;
      const placeholder = this.activeFocusPlaceholder;
      const escaped = placeholder.replace(/[.*+?^${}()|[\]\\]/g, (c) => '\\' + c);
      return html.replace(
        new RegExp(escaped, 'g'),
        `<span style="background:#fef08a;padding:0 2px;border-radius:2px;border-bottom:2px solid #eab308;color:#854d0e;">${placeholder}</span>`,
      );
    }


  // ══════════════════════════════════════════════════════════════════════════
  // Media URL helpers
  // ══════════════════════════════════════════════════════════════════════════
  isValidMediaUrl(url: string): boolean {
    const u = (url || '').trim();
    return !!u && !u.includes('#mediaUrl');
  }

  getPdfFileNameFromUrl(url?: string): string {
    if (!url) return '';
    try {
      const name = url
        .slice(url.lastIndexOf('/') + 1, url.lastIndexOf('_'))
        ?.replaceAll('%20', ' ');
      const arr = url.split('.');
      return `${name}.${arr[arr.length - 1]}`;
    } catch {
      return 'Document.pdf';
    }
  }

  // ── Carousel media display helpers ────────────────────────────────────────
  shouldShowCarouselMedia(card: any): boolean {
    return (
      !!(card.mediaURL && this.isValidMediaUrl(card.mediaURL)) ||
      !!card.mediaType
    );
  }

  isCarouselImage(card: any): boolean {
    const t = (card.carouselMediaType || card.mediaType || '').toLowerCase();
    return (
      t === 'image' && !!card.mediaURL && this.isValidMediaUrl(card.mediaURL)
    );
  }

  isCarouselVideo(card: any): boolean {
    const t = (card.carouselMediaType || card.mediaType || '').toLowerCase();
    return (
      t === 'video' && !!card.mediaURL && this.isValidMediaUrl(card.mediaURL)
    );
  }

  isCarouselDocument(card: any): boolean {
    const t = (card.carouselMediaType || card.mediaType || '').toLowerCase();
    return t === 'doc' || t === 'document';
  }

  // ── RCS carousel media helpers (mirrors legacy component exactly) ─────────
  isCarouselDynamicMedia(card: any): boolean {
    return !!(
      (!card.mediaURL || card.mediaURL?.includes('#mediaUrl')) &&
      (!card.videoThumbnail || card.videoThumbnail?.length === 0) &&
      !card.staticThumbnailUrl?.length &&
      card.staticUrl?.length
    );
  }

  isCarouselDynamicThumbnail(card: any): boolean {
    return !!(
      (card.carouselMediaType || card.mediaType)?.toUpperCase() ===
        MEDIA_TYPE.VIDEO &&
      (!card.videoThumbnail?.length ||
        card.videoThumbnail?.includes('#thumbnail')) &&
      card.staticThumbnailUrl?.length
    );
  }

  hasCarouselVideoThumbnail(card: any): boolean {
    return !!(
      (card.carouselMediaType || card.mediaType)?.toUpperCase() ===
        MEDIA_TYPE.VIDEO &&
      card.videoThumbnail &&
      !card.videoThumbnail?.includes('#thumbnail') &&
      (!card.staticThumbnailUrl || card.staticThumbnailUrl?.length === 0)
    );
  }

  isCarouselVideoPlayable(card: any): boolean {
    return !!(
      (card.carouselMediaType || card.mediaType)?.toUpperCase() ===
        MEDIA_TYPE.VIDEO &&
      card.mediaURL &&
      (!card.videoThumbnail || card.videoThumbnail?.includes('#thumbnail')) &&
      !card.staticThumbnailUrl
    );
  }

  // ── RCS standalone card media helpers (mirrors legacy component exactly) ──
  /** Has a static video thumbnail (not dynamic) */
  hasVideoThumbnail(fd: any): boolean {
    return !!(
      fd.mediaType === MEDIA_TYPE.VIDEO &&
      fd.videoThumbnail &&
      !fd.videoThumbnail?.includes('#thumbnail1#') &&
      !fd.staticThumbnailUrl?.length &&
      fd.videoThumbnail.length
    );
  }

  /** Dynamic thumbnail: placeholder token present AND we have staticThumbnailUrl */
  isDynamicThumbnail(fd: any): boolean {
    return !!(
      fd.mediaType === MEDIA_TYPE.VIDEO &&
      fd.videoThumbnail?.includes('#thumbnail1#') &&
      fd.staticThumbnailUrl?.length
    );
  }

  /** Dynamic media: mediaURL is a placeholder or empty AND no thumbnail present AND isMedia is true */
  isDynamicMedia(fd: any): boolean {
    const url: string = fd.mediaUrl || '';
    const isDynUrl = !url || url.includes('#mediaUrl');
    const hasNoThumb = !fd.videoThumbnail?.length;
    // Show dynamic media placeholder when: URL is dynamic/missing, no thumbnail,
    // and the card is marked as having media (isMedia flag or staticUrl present)
    return !!(isDynUrl && hasNoThumb && fd.isMedia);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Button action helpers — normalised comparison
  // ══════════════════════════════════════════════════════════════════════════
  normalizeButtonAction(action: any): string {
    return (action || '').toString().trim().toUpperCase().replace(/ /g, '_');
  }

  isUrlButton(a: any): boolean {
    const n = this.normalizeButtonAction(a);
    return (
      n === 'OPEN_URL' ||
      n === 'WEB_FORM_URL' ||
      n === 'STATIC_CTA' ||
      n === 'DYNAMIC_CTA'
    );
  }

  isReplyButton(a: any): boolean {
    return this.normalizeButtonAction(a) === 'QUICK_REPLY';
  }

  isCallButton(a: any): boolean {
    const n = this.normalizeButtonAction(a);
    return (
      n === 'CALL_BACK' || n === 'STATIC_CALLBACK' || n === 'DYNAMIC_CALLBACK'
    );
  }

  isLocationButton(a: any): boolean {
    const n = this.normalizeButtonAction(a);
    return (
      n === 'LOCATION' || n === 'SHOW_LOCATION' || n === 'CREATE_SHOW_LOCATION'
    );
  }

  isCalendarButton(a: any): boolean {
    const n = this.normalizeButtonAction(a);
    return n === 'CALENDAR' || n === 'CREATE_CALENDAR_EVENT';
  }

  /** Label for a button — handles both field naming conventions */
  getButtonLabel(btn: any): string {
    return btn?.buttonLabel || btn?.label || btn?.displayText || '';
  }

  openButtonAction(button: any): void {
    const url = this.getButtonUrl(button, button?.buttonAction);
    if (url) window.open(url, '_blank');
  }

  private getButtonUrl(button: any, action: string): string | undefined {
    const n = this.normalizeButtonAction(action);
    const prefixes = ['http://', 'https://'];
    switch (n) {
      case 'STATIC_CTA': {
        let url = (button?.url || button?.buttonValue)?.trim?.() ?? '';
        if (url && !prefixes.some((p) => url.startsWith(p))) {
          url = `https://${url}`;
        }
        return url || undefined;
      }
      case 'DYNAMIC_CTA': {
        let url = button?.shortenerProvider ? button.baseUrl : button?.url;
        if (url && typeof url === 'string') {
          url = url.trim();
          if (!prefixes.some((p) => url.startsWith(p))) {
            url = `https://${url}`;
          }
        }
        return url || undefined;
      }
      case 'WEB_FORM_URL': {
        const fc = button?.buttonValue || button?.formCode;
        return fc
          ? `${environment.FORM_PREVIEW_URL}/form/${fc}/PREVIEW/`
          : undefined;
      }
      default:
        return undefined;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Email helpers (mirrors PreviewEmailTemplateComponent)
  // ══════════════════════════════════════════════════════════════════════════
  get emailMasterJson(): any {
    return this.templateData?.templateJson || {};
  }

  isImageContent(content: any): boolean {
    return content?.type === 'IMAGE';
  }
  isButtonContent(content: any): boolean {
    return content?.type === 'BUTTON';
  }
  isTextContent(content: any): boolean {
    return content?.type === 'TEXT';
  }

  checkContentInfo(index: number): boolean {
    const rows = this.emailMasterJson?.layout?.rows;
    if (rows?.[index]?.columns?.length) {
      for (const col of rows[index].columns) {
        if (col.content) return false;
      }
    }
    return true;
  }

  getButtonStyleClasses(properties: any): string {
    const map: Record<string, string> = {
      Small: 'p-button p-button-sm',
      Medium: 'p-button',
      Large: 'p-button p-button-lg',
    };
    let cls = properties?.size ? (map[properties.size] ?? '') : '';
    if (properties?._highlightState === 'focused') {
      cls += ' email-btn-focused';
    } else if (properties?._highlightState === 'mapped') {
      cls += ' email-btn-mapped';
    }
    return cls;
  }

  getHref(actionUrl: string | undefined): boolean {
    if (!actionUrl) return false;
    return (
      actionUrl.trim().startsWith('http://') ||
      actionUrl.trim().startsWith('https://')
    );
  }

  // Mirrors PreviewEmailTemplateComponent.getFormHref
  getFormEmailHref(properties: any): string {
    const actionUrl = properties?.actionUrl?.trim();
    if (this.getHref(actionUrl)) {
      return properties?.paramPlaceholder?.trim() || '#';
    }
    if (properties?.selectedWebForm) {
      const parts = properties.selectedWebForm.split('|');
      const code = parts[parts.length - 1];
      return `${environment.FORM_PREVIEW_URL}/form/${code}/PREVIEW/`;
    }
    return '#';
  }

  // Mirrors PreviewEmailTemplateComponent.getUpdatedFormNameContent
  getUpdatedFormNameContent(properties: any): string {
    // Support both properties.htmlText (Text-content) and properties.text (Button-content)
    const text: string = properties?.htmlText || properties?.text || '';
    if (!text) return '';

    let res = text;

    // Replace form URL tokens
    if (properties?.attachedFormName && properties?.attachedFormCode) {
      const formUrl = `<a href="${environment.FORM_PREVIEW_URL}/form/${properties.attachedFormCode}/PREVIEW" target="_blank">${properties.attachedFormName} (${properties.attachedFormCode})</a>`;
      if (properties?.urlShorten) {
        res = res.replace('#form_url_tiny#', formUrl);
      } else {
        res = res.replace('#form_url#', formUrl);
      }
    } else if (properties?.selectedWebForm) {
      const parts = properties.selectedWebForm.split('|');
      const name = parts[0];
      const code = parts[parts.length - 1];
      const formUrl = `<a href="${environment.FORM_PREVIEW_URL}/form/${code}/PREVIEW" target="_blank">${name} (${code})</a>`;
      res = res
        .replace('#form_url#', formUrl)
        .replace('#form_url_tiny#', formUrl);
    }

    return this.applyFocusHighlight(res);
  }

  // Button label for email buttons (should NOT use htmlText — just text)
  getEmailButtonLabel(properties: any): string {
    return this.applyFocusHighlight(
      properties?.text || properties?.buttonText || '',
    );
  }
}
